import { Component, Input, OnInit, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserEmailsSearchComponent } from '../../shared';
import { EmailControl } from './email-control';
import { EscalationMailDrop, EscalationMailFormArray } from './escalation-mail-form-array';
import { EscalationMailEmitter } from './escalation-mail-emitter';
import {EQueryAccessionInfo} from '../../shared/e-query-accession-info';


interface EmailDragEvent extends DragEvent {
    target: HTMLInputElement;
}
@Component({
    selector: 'escalation-mail',
    template: require('./escalation-mail.component.html'),
    styles: [require('./escalation-mail.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EscalationMailComponent),
            multi: true
        }
    ]
})

export class EscalationMailComponent implements OnInit, ControlValueAccessor {

    @Input()
    public disabled = false;
    @Input()
    public required = false;
    @Input()
    public escalationName: string;
    @Input()
    public customErrorMessage: string;
    @Input()
    public accessionNumber: string;
    @Output()
    public valid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public isFirstValid: EventEmitter<boolean> = new EventEmitter<boolean>();

    public get value() {
        return this.val;
    }

    @Input()
    public set value(inputVal) {
        this.val = inputVal;
        this.onChange(this.val);
    }

    public readonly mailsMaxCount = 20;
    public readonly mailsMinCount = 1;
    public readonly formMails = new EscalationMailFormArray();
    public searchedMail: string;
    private val: string[] = [];
    private readonly escalationKey = 'escalation';
    private readonly escalationMail = 'escalationMail';

    @ViewChild(UserEmailsSearchComponent)
    private userEmailsSearchComponent: UserEmailsSearchComponent;

    constructor(private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly escalationMailEmitter: EscalationMailEmitter) {
            escalationMailEmitter.$escalationMailDrop.subscribe(
            escalationMail => {
                if (escalationMail
                    && escalationMail.dropFrom === this.escalationName
                    && escalationMail.dropTo !== this.escalationName) {
                    const control = this.formMails.controls.find(data => data.value === escalationMail.mail);
                    this.deleteElement(control);
                }
            });
    }

    public onChange: any = () => { };
    public onTouched: any = () => { };

    public ngOnInit(): void {
        if (this.value && this.value.length > 0) {
            this.value.forEach(mail => {
                this.formMails.push(new EmailControl(mail, this.formMails));
            });
            this.valueChanged();
        }
    }

    public ngOnDestroy() {
        this.escalationMailEmitter.$escalationMailDrop.unsubscribe();
    }

    public valueChanged(selectedMail: string = '', elementIndex = 0) {
        if (selectedMail) {
            this.formMails.controls[elementIndex].setValue(selectedMail);
        }

        if (this.formMails.valid) {
            this.value = this.formMails.getRawValue();
        }
        this.onChange(this.formMails.value);
        this.isFirstValid.emit(this.formMails.controls.length > 0 ? this.formMails.controls[0].valid : false);
        this.valid.emit(this.formMails.valid);
        this.changeDetectorRef.markForCheck();
    }

    public searchedMailChanged(searchedMail: string) {
        this.searchedMail = searchedMail;
    }

    public isDuplicateEmail(fileIndex: number): boolean {
        return this.formMails.getByIndex(fileIndex).hasDuplidateEmailError;
    }

    public addMail(mail: string = this.searchedMail) {
        if (mail) {
            this.formMails.push(new EmailControl(mail, this.formMails));
            this.valueChanged();
            this.userEmailsSearchComponent.clearValue();
            this.searchedMail = '';
        }
    }

    public deleteElement(formData: AbstractControl) {
        this.formMails.removeAt(this.formMails.value.findIndex((data: string) => data === formData.value));
        this.valueChanged();
    }

    public writeValue(value: string[]): void {
        this.value = value;
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public isValid(control: FormControl, index: number): boolean {
        if (index === 0
            && control.value === ''
            && !this.required) {
            return false;
        }
        return control.invalid;
    }

    public onDragStart(event: EmailDragEvent) {
        event
            .dataTransfer
            .setData(this.escalationMail, event.target.id);
        event
            .dataTransfer
            .setData(this.escalationKey, this.escalationName);
    }

    public onDragOver(event: EmailDragEvent) {
        event.preventDefault();
    }

    public onDrop(event: EmailDragEvent) {
        const mail = event.dataTransfer.getData(this.escalationMail);
        const dropFrom = event.dataTransfer.getData(this.escalationKey);
        if (mail
         && dropFrom
         && dropFrom !== this.escalationName
         && this.formMails.valid
         && this.formMails.controls.length < this.mailsMaxCount
         && !this.isDuplicateMail(mail)
         && !this.disabled) {
            const escalationMailDrop = new EscalationMailDrop();
            escalationMailDrop.dropFrom = dropFrom;
            escalationMailDrop.dropTo = this.escalationName;
            escalationMailDrop.mail = mail;
            this.addMail(mail);
            this.escalationMailEmitter.setMailDrop(escalationMailDrop);
        }
        event.dataTransfer.clearData();
    }

    private isDuplicateMail(mail: string): boolean {
        if (mail) {
            const mails = this.formMails.controls.map(s => s.value);
            return mails.indexOf(mail) >= 0;
        }
        return false;
    }
}
